import { t } from "i18next";
import { useRouter } from "next/router";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getItemEventDetail } from "../../api/match.api";

import { cancelOrder, getOldOrder } from "../../api/order.api";
import { getAllTicketByEvent } from "../../api/ticket.api";
import BaseImage from "../../components/baseImage/BaseImage";
import { EventDetailContent } from "../../components/events";
import MatchBookNowCard from "../../components/events/MatchBookNowCard";
import EventDetailLoading from "../../components/events/skeletonLoading/EventDetailLoading";
import MatchInfoLoading from "../../components/events/skeletonLoading/MatchInfoLoading";
import ChooseYourTicket from "../../components/events/tabs/chooseYourTicket";
import CompletePayment from "../../components/events/tabs/completePayment";
import StepWithDivide from "../../components/step";
import { AuthContext } from "../../contexts/AuthContext";
import { DataContext } from "../../contexts/DataContext";
import withAuth from "../../hocs/AuthHoc";
import { useLanguage } from "../../hooks/useLanguage";
import { fromNowDateWithLocal } from "../../utils/date";
import { delayMs } from "../../utils/delay";
import { showToast } from "../../utils/showToast";
import { FOOD_COURT } from "../../constant/constants";

function EventDetail() {
  const router = useRouter();

  const { user } = useContext(AuthContext);
  const { currentOrder, onResetData, setTicketUserBuy, settings } =
    useContext(DataContext);
  const [isLoadingFetchTicketType, setIsLoadingFetchTicketType] =
    useState(true);

  const { currentLanguage, nameWithLanguage } = useLanguage();
  const [ticketTypes, setTicketTypes] = useState([]);
  const [isLoadingSetOldOrder, setIsLoadingOldOrder] = useState(true);
  const [isLoadingCancelOrder, setLoadingCancelOrder] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [itemDetail, setItemDetail] = useState(null);
  const refIntervalTimeOrder = useRef(null);

  const eventId = itemDetail?.id;

  const [isLoadingItemDetail, setIsLoadingItemDetail] = useState(true);
  const [oldOrder, setOldOrder] = useState(null);
  const [timerOrder, setTimerSeconds] = useState(0);
  const displaySeconds = String(Math.floor(timerOrder % 60)).padStart(2, "0");
  const displayMinutes = String(Math.floor(timerOrder / 60)).padStart(2, "0");
  const orderCountDown = { displaySeconds, displayMinutes };
  const isHadOldOrder =
    oldOrder?.isExist && oldOrder?.order?.status === "PENDING";
  const slug = router?.query?.slug || null;

  const onGetOldOrder = useCallback(async (eventId) => {
    try {
      const response = await getOldOrder(eventId);
      setOldOrder(response.data);
      if (response.data.isExist && response.data?.order?.status === "PENDING") {
        const prepareDataOrder = response.data?.order?.orderItems?.map(
          (itemOldOrder) => {
            const { quantity } = itemOldOrder;

            return {
              quantity,
              ...itemOldOrder?.ticketType,
              coupon: itemOldOrder?.coupon,
            };
          }
        );

        setTicketUserBuy(prepareDataOrder);
      }
      setIsLoadingOldOrder(false);
    } catch (error) {
      setIsLoadingOldOrder(false);
    }
  }, []);

  const onFetchTicketByEvent = useCallback(async () => {
    try {
      setIsLoadingFetchTicketType(true);
      const response = await getAllTicketByEvent(eventId);
      delayMs(650).then(() => setIsLoadingFetchTicketType(false));
      setTicketTypes(response.data);
    } catch (error) {
      delayMs(650).then(() => setIsLoadingFetchTicketType(false));
    }
  }, [eventId]);
  const handleCancelOrder = useCallback(async () => {
    try {
      if (isLoadingCancelOrder) return;
      setLoadingCancelOrder(true);
      const response = await cancelOrder(oldOrder?.order?.id);
      if (response.status === 200) {
        setTicketUserBuy([]);
        setOldOrder(null);
      }

      if (refIntervalTimeOrder.current) {
        clearInterval(refIntervalTimeOrder.current);
      }
      setTimerSeconds(0);
      setCurrentStep(1);
      onFetchTicketByEvent();
      setLoadingCancelOrder(false);
      showToast({ message: "Cancel Order success", type: "success" });
    } catch (error) {
      console.log("error", error);
      setLoadingCancelOrder(false);
      showToast({ message: "Cancel Order failed!", type: "error" });
    }
  }, [
    oldOrder?.order?.id,
    isLoadingCancelOrder,
    setTicketUserBuy,
    onFetchTicketByEvent,
    setOldOrder,
    refIntervalTimeOrder.current,
  ]);

  const timeoutSetting = useMemo(() => {
    const timeoutSetting = settings?.find(
      (setting) => setting?.key === "TIMEOUT_ORDER"
    );
    return timeoutSetting;
  }, [settings]);

  useEffect(() => {
    if (oldOrder?.order?.status === "PENDING") {
      refIntervalTimeOrder.current = setInterval(() => {
        const asSecond = fromNowDateWithLocal(oldOrder?.order?.createdAt);
        const totalSecondCountDown =
          Math.abs(timeoutSetting?.content?.limit * 60) - Math.abs(asSecond);

        setTimerSeconds(totalSecondCountDown);
        if (totalSecondCountDown <= 0) {
          clearInterval(refIntervalTimeOrder.current);
        }
      }, 1000);
    }
  }, [oldOrder?.order]);

  useEffect(() => {
    if (eventId && user) onGetOldOrder(eventId);
  }, [eventId, onGetOldOrder, user]);

  useEffect(() => {
    if (eventId && user) onFetchTicketByEvent();
  }, [eventId, onFetchTicketByEvent, user]);

  const onFetchDataEventDetail = useCallback(async () => {
    try {
      const response = await getItemEventDetail(slug);

      if (response?.status === 200) {
        setItemDetail(response.data);

        delayMs(650).then(() => {
          setIsLoadingItemDetail(false);
        });
      }
    } catch (error) {
      console.log("error", error.message);
      if (error?.message === "Request failed with status code 400") {
        router.replace("/404");
      }
      // delayMs(650).then(() => {
      //   setIsLoadingItemDetail(false);
      // });
    }
  }, [slug]);

  useEffect(() => {
    if (slug) onFetchDataEventDetail();
  }, [slug, onFetchDataEventDetail]);

  useEffect(() => {
    if (slug !== currentOrder?.slug) {
      console.log("reset-data");
      onResetData();
    }
  }, [slug, currentOrder, onResetData]);

  const data = [
    { title: t("event_details"), id: "0" },
    { title: t("choose_your_ticket"), id: "1" },
    { title: t("complete_payment"), id: "2" },
  ];

  const onNextStep = useCallback(() => {
    if (!user && itemDetail?.category !== FOOD_COURT) {
      router.push({
        pathname: "/login",
        query: { event: `event/${router.query.slug}` },
      });
      return;
    }
    if (!user && itemDetail?.category === FOOD_COURT) {
      router.push({
        pathname: "/foodcourt-event",
        query: { event: `event/${router.query.slug}` },
      });
      return;
    }
    if (itemDetail?.isDraft) {
      router.replace("/");
      return;
    }
    if (currentStep < data.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, router, data.length, user]);

  const timeHadOrder =
    oldOrder?.isExist && currentLanguage === "en"
      ? `${orderCountDown.displayMinutes} : ${orderCountDown.displaySeconds}`
      : `${orderCountDown.displaySeconds} : ${orderCountDown.displayMinutes}`;

  const orderTicketType = ticketTypes.sort((a, b) =>
    a[nameWithLanguage].localeCompare(b[nameWithLanguage])
  );
  const renderContentEvent = useCallback(() => {
    switch (currentStep) {
      case 0:
        return <EventDetailContent data={itemDetail} />;
      case 1:
        return (
          <ChooseYourTicket
            timeHadOrder={timeHadOrder}
            isLoadingSetOldOrder={isLoadingSetOldOrder}
            oldOrder={oldOrder}
            setOldOrder={setOldOrder}
            dataOrder={itemDetail}
            eventId={eventId}
            onNextStep={onNextStep}
            ticketTypes={orderTicketType}
            isLoadingFetchTicketType={isLoadingFetchTicketType}
            onFetchTicketByEvent={onFetchTicketByEvent}
            onGetOldOrder={onGetOldOrder}
            handleCancelOrder={handleCancelOrder}
          />
        );
      case 2:
        return (
          <CompletePayment
            timeHadOrder={timeHadOrder}
            oldOrder={oldOrder}
            handleCancelOrder={handleCancelOrder}
          />
        );
      default:
        return null;
    }
  }, [
    currentStep,
    eventId,
    onNextStep,
    itemDetail,
    isLoadingFetchTicketType,
    onFetchTicketByEvent,
    orderTicketType,
    oldOrder,
    isLoadingSetOldOrder,
    timeHadOrder,
    handleCancelOrder,
    onGetOldOrder,
  ]);

  return (
    <div className="flex flex-col items-center justify-center flex-1 w-full mt-6 bg-secondary-100">
      <div className="relative hidden w-full overflow-hidden md:inline sm:mx-10 lg:mx-20 h-60 sm:h-96 md:px-10 lg:h-53">
        <BaseImage
          backgroundSize="cover"
          src={itemDetail?.eventDetailImage}
          className={"md:rounded-16"}
        />
        <div className="absolute top-0 w-full h-full md:rounded-t-lg opacity-80" />
      </div>
      <div className="flex flex-col w-full px-4 mt-6 sm:px-10 gap-y-6 md:pt-8">
        <div className="w-full px-16 py-6 md:py-6 2xl:px-48 md:px-24 lg:px-40 sm:px-20 sm:block bg-secondary-200 rounded-70">
          <div className="flex flex-row pb-5">
            {data?.map((item, index) => {
              return (
                <StepWithDivide
                  item={item}
                  key={index}
                  step={index}
                  currentStep={currentStep}
                  totalStep={data?.length}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-col-reverse w-full pt-6 md:pt-10 lg:pt-16 md:px-10 lg:px-10 lg:grid lg:grid-cols-12 ">
        <div className="row-span-1 px-4 pb-10 lg:px-0 md:px-0 md:mt-0 lg:grid lg:col-span-8">
          {isLoadingItemDetail ? <EventDetailLoading /> : renderContentEvent()}
        </div>
        <div className="relative w-full row-span-1 md:rounded-t-lg md:grid lg:col-span-4 ">
          <div className="lg:pb-10 md:pb-10 ">
            {isLoadingItemDetail ? (
              <MatchInfoLoading />
            ) : (
              <div className="flex flex-row w-full">
                <div className="hidden w-0 lg:w-6 lg:block" />
                <MatchBookNowCard
                  orderCountDown={orderCountDown}
                  oldOrder={oldOrder}
                  isHadOldOrder={isHadOldOrder}
                  currentStep={currentStep}
                  onBookNow={onNextStep}
                  onCompletePayment={currentStep}
                  bgImageUrl={itemDetail?.image}
                  itemDetail={itemDetail}
                  isHasTicketType={itemDetail?.isHasTicketType}
                  ticketTypes={orderTicketType}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withAuth(EventDetail);
